import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { DefaultTheme, useTheme } from "styled-components/native";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { markupAmount, markupAmountTrimmed, markupSchema } from "./utils";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { useUpdateUser } from "@apis/user";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ToastTypes, showToast } from "../VDBToast/util";
import styled from "styled-components/native";
import { useTranslation } from "react-i18next";
import { Keyboard, TouchableOpacity, View } from "react-native";
import {
  CardProps,
  ChangeMarkupForProductProps,
  ChangePriceMarkupInputConatinerProps,
  ChangePriceMarkupModalProps,
  ProductWiseMarkupData,
} from "./types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import Modal from "@components/atoms/Modal";
import ContactUsModal from "../ContactUs";
import { css } from "styled-components/native";
import LineView from "@scenes/requests/utils/LineView";
import { useSelector } from "react-redux";
import { VDBIcons } from "@utils/VDBIcons";
import { useQueryClient } from "@tanstack/react-query";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { PriceModeEventType } from "@utils/analytics.web";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";

const ChangeMarkupForProduct = ({
  closeModal,
  setToastMessage,
}: ChangeMarkupForProductProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const useUser = useUserInfo();
  const [openModal, setOpenModal] = useState(false);
  const [priceMarkupChangeModalType, setPriceMarkupChangeModalType] =
    useState("");
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const { t } = useTranslation();

  const productWiseMarkupData: ProductWiseMarkupData = {
    diamond: {
      productTitle: t("priceMode.productWiseMarkup.naturalDiamonds.title"),
      category: "diam",
      markup: null,
    },
    lab_grown_diamond: {
      productTitle: t("priceMode.productWiseMarkup.labgrownDiamonds.title"),
      category: "lab",
      markup: null,
    },
    gemstone: {
      productTitle: t("priceMode.productWiseMarkup.gemstone.title"),
      category: "gem",
      markup: null,
    },
    jewelry: {
      productTitle: t("priceMode.productWiseMarkup.jewelry.title"),
      category: "jewelry",
      markup: null,
    },
  };

  const openChangePriceModal = (type: string) => {
    setOpenModal(true);
    setPriceMarkupChangeModalType(type);
  };

  const onModalClose = () => {
    setOpenModal(false);
  };

  const allowedStockTypes = useMemo(
    () => useUser?.getAllowedStockTypes() || [],
    [useUser]
  );

  const mapping = {
    diamond: "diamond_markup",
    lab_grown_diamond: "lab_grown_diamond_markup",
    gemstone: "gemstone_markup",
    jewelry: "jewelry_markup",
  };

  const productsMarkUp = useMemo(() => {
    if (
      useUser.user.product_markups !== null &&
      useUser.user.feature_settings.enable_product_level_markup
    ) {
      let groupSection = {};
      Object.keys(mapping).forEach((key) => {
        const markupKey = mapping[key];
        if (useUser.user.product_markups[markupKey]) {
          groupSection[markupKey] = useUser.user.product_markups[markupKey];
        } else {
          groupSection[markupKey] = 0;
        }
      });
      const filteredData = Object.keys(groupSection)
        .filter((key) => allowedStockTypes.includes(key.replace("_markup", "")))
        .reduce((acc, key) => {
          acc[key] = groupSection[key];
          return acc;
        }, {});
      return filteredData;
    }
  }, [theme, allowedStockTypes]);

  return (
    <>
      {!productsMarkUp ? (
        <Container>
          {!(
            useUser?.user?.feature_settings
              ?.users_can_set_markup_when_locked_in_orange_mode &&
            useUser?.user?.is_price_mode_lock
          ) && (
            <>
              <VDBText type={typography["H2-M"]} color={theme["core-05"]}>
                {useUser.user.markup_unit === "X" ||
                useUser.user.markup_unit === "x"
                  ? t("priceMode.multiplierMarkup.title", "Multiplier Markup")
                  : t("priceMode.priceMarkup.title")}
              </VDBText>
              <Spacer size={SpaceType.Basic} />
            </>
          )}
          <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
            {useUser.user.markup_unit === "X" ||
            useUser.user.markup_unit === "x"
              ? t(
                  "priceMode.multiplierMarkup.description",
                  "Enter the markup multiplier that you would like added to stone's base price."
                )
              : t("priceMode.priceMarkup.description")}
          </VDBText>
          <Spacer size={SpaceType.Big} />
          <ChangePriceMarkupInputConatiner
            markup={useUser?.user?.markup_amount}
            onClose={() => {
              theme.isLargeScreen && closeModal?.();
            }}
          />
        </Container>
      ) : (
        <>
          {productsMarkUp &&
            Object.keys(productsMarkUp).length &&
            Object.keys(productsMarkUp).map((productMarkup, i) => {
              const key = productMarkup.replace(
                "_markup",
                ""
              ) as keyof ProductWiseMarkupData;

              productWiseMarkupData[key].markup =
                productsMarkUp?.[productMarkup]?.toString() ?? "0.0";
              productMarkup = productMarkup.replace("_markup", "");
              productWiseMarkupData[productMarkup].product_type = productMarkup;
              const currentProductWiseMarkup =
                productWiseMarkupData[productMarkup];
              return (
                <Container key={i}>
                  <VDBText
                    type={typography["H3-M"]}
                    color={
                      theme[
                        `${currentProductWiseMarkup.category}-01` as keyof DefaultTheme
                      ] as string
                    }
                  >
                    {currentProductWiseMarkup.productTitle}
                  </VDBText>
                  <Card
                    backgroundColor={theme["core-01"]}
                    borderColor={theme["core-03"]}
                    onPress={() => openChangePriceModal(productMarkup)}
                  >
                    <Container>
                      <CardMain>
                        <VDBText
                          type={typography["Body-R"]}
                          color={theme["core-05"]}
                        >
                          {currentProductWiseMarkup?.markup
                            ? t("priceMode.productWiseMarkup.priceMarkup.text")
                            : t(
                                "priceMode.productWiseMarkup.choosePriceMarkup.text"
                              )}
                        </VDBText>
                        <ActiveView>
                          <VDBText
                            type={typography["Body-R"]}
                            color={theme["core-04"]}
                          >
                            {currentProductWiseMarkup?.markup &&
                              markupAmount(
                                currentProductWiseMarkup.markup,
                                useUser.user.markup_unit
                              )}
                          </VDBText>
                          <View>
                            <VDBIcon
                              name={VDBIcons.ARROW_RIGHT}
                              variant={IconsVariant.VDBCustom}
                              size={SpaceType.Big}
                              color={theme["core-04"]}
                            />
                          </View>
                        </ActiveView>
                      </CardMain>
                    </Container>
                  </Card>
                </Container>
              );
            })}
          {openModal && (
            <ChangePriceMarkupModal
              markupData={productWiseMarkupData[priceMarkupChangeModalType]}
              isOpen={openModal}
              onModalClose={onModalClose}
              markupUnit={useUser.user.markup_unit}
              setToastMessage={setToastMessage}
            />
          )}
        </>
      )}
      <Container>
        <LineView background={theme["core-03"]} />
        <ContactUsContainer>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {t("priceMode.priceMarkup.retailModeFooter.text").replace(
              "{{contact_us}}",
              ""
            )}
            <VDBText
              type={typography["Minor-M"]}
              color={theme["link-static"]}
              onPress={() => {
                setShowContactUsModal(true);
              }}
            >
              {t("contactUs.title.text")}
            </VDBText>
          </VDBText>

          <Spacer size={SpaceType.Basic} />
          <ContactUsModal
            isOpen={showContactUsModal}
            onClose={() => setShowContactUsModal(false)}
            screenName="Change Price MarkUp"
          />
        </ContactUsContainer>
      </Container>
    </>
  );
};

const ContactUsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding-bottom: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Double}px;
`;

const ChangePriceMarkupModal = ({
  markupData,
  isOpen,
  onModalClose,
  markupUnit,
  setToastMessage,
}: ChangePriceMarkupModalProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Modal
      headerTitle={markupData.productTitle}
      isOpen={isOpen}
      onClose={onModalClose}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={40}
      paddingTop={16}
    >
      <Overlay>
        <ScreenText>
          <VDBText
            type={typography["H2-M"]}
            color={theme["core-05"]}
            textAlign="left"
          >
            {markupUnit === "X" || markupUnit === "x"
              ? t(
                  "priceMode.multiplierMarkup.modal.title.text",
                  "Multiplier Markup"
                )
              : t("priceMode.priceMarkup.modal.title.text")}
          </VDBText>
        </ScreenText>

        <Spacer size={SpaceType.Basic} />
        <TextContainer>
          <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
            {markupUnit === "X" || markupUnit === "x"
              ? t(
                  "priceMode.multiplierMarkup.modal.subTitle",
                  "Enter the markup multiplier that you would like added to stone's base price."
                )
              : t("priceMode.priceMarkup.modal.subTitle")}
          </VDBText>
        </TextContainer>
        <Spacer size={SpaceType.Big} />
        <CenterContainer>
          <ChangePriceMarkupInputConatiner
            markup={markupData.markup}
            product_type={markupData.product_type}
            onClose={onModalClose}
            setToastMessage={setToastMessage}
          />
        </CenterContainer>
      </Overlay>
    </Modal>
  );
};

const ChangePriceMarkupInputConatiner = ({
  markup,
  product_type,
  onClose,
  setToastMessage,
}: ChangePriceMarkupInputConatinerProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const useUser = useUserInfo();
  const { t } = useTranslation();
  const updateUser = useUpdateUser(useUser.getUserId());
  const [enableApplyButton, setEnableApplyButton] = useState(false);
  const { control, handleSubmit, setValue, getValues } = useForm({
    resolver: yupResolver(markupSchema),
  });
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const queryClient = useQueryClient();
  const { trackWebEvent } = useUserAnalytics();
  const [trigger, setTrigger] = useState(false);
  const initialMarkupValue = useRef(getValues("markup"));

  useEffect(() => {
    if (useUser.userCanSetMarkupInOrangeMode() && useUser.isPriceModeLock()) {
      trackWebEvent(PriceModeEventType.ORANGE_MODE_MARKUP, {
        can_set_markup: true,
      });
    }
  }, []);

  useEffect(() => {
    if (markup) {
      const calculatedMarkup = markupAmount(markup, useUser.user.markup_unit);
      setValue("markup", calculatedMarkup);

      initialMarkupValue.current = markup;

      if (trigger) {
        setTrigger(false);
      }
      setEnableApplyButton(false);
    }
  }, [markup, trigger]);

  const onSubmit = (data: any) => {
    Keyboard.dismiss();
    const productMarkup = markupAmountTrimmed(
      data.markup,
      useUser.user.markup_unit
    );
    let userBody: any = {
      markup_amount: productMarkup,
      installation_identifier: installationIdentifier,
    };
    if (product_type) userBody.product_type = product_type;

    trackWebEvent(PriceModeEventType.ADD_MARKUP, {
      product_type: product_type,
      new_markup_value: productMarkup,
    });

    updateUser.mutate(
      {
        user: userBody,
      },
      {
        onSuccess(successData) {
          queryClient.resetQueries({ queryKey: ["SEARCH_RESULTS"] }).then();
          if (product_type) {
            if (theme.isLargeScreen) {
              setToastMessage?.(successData.data.message ?? "");
            } else {
              showToast(
                t("priceMode.priceMarkup.toastMessageSuccess.title"),
                successData.data.message,
                ToastTypes.SUCCESS,
                true,
                true
              );
            }
            onClose?.();
          } else {
            showToast(
              t("priceMode.priceMarkup.toastMessageSuccess.title"),
              successData.data.message,
              ToastTypes.SUCCESS,
              true,
              true
            );
            onClose?.();
          }

          const currentValue = getValues("markup");
          if (initialMarkupValue.current !== currentValue) {
            setTrigger(true);
            initialMarkupValue.current = currentValue;
          } else {
            setTrigger(false);
          }
        },
      }
    );
  };
  const onChangeText = (text: any, onChange: any) => {
    const lastChar = text.slice(-1);
    if (lastChar === ",") {
      text = text.slice(0, -1) + ".";
    }
    text = text.replace(/,/g, "");

    const markupUnit = useUser.user.markup_unit;
    if (markupUnit === "X" || markupUnit === "x") {
      text = text.replace(/^[Xx]\s*/, "");
      text = text.replace(/^(\d{0,3}(?:\.\d{0,6})?).*$/, "$1");
      const numericValue = parseFloat(text);
      if (!isNaN(numericValue) && numericValue < 1) {
        text = "1";
      } else if (!isNaN(numericValue) && numericValue > 100) {
        text = "100";
      }
    } else {
      text = text.replace(/^(\d{0,5}(?:\.\d{0,2})?).*$/, "$1");
    }

    const amount = markupAmountTrimmed(text, markupUnit);
    const isMultiplierUnit = markupUnit === "X" || markupUnit === "x";
    const minMarkupValue = isMultiplierUnit ? 1 : 0;

    onChange(text);
    setEnableApplyButton(
      !!amount &&
        Number(amount) >= minMarkupValue &&
        !!markup &&
        formatNumberWithPrecision(amount, {
          precision: markupUnit === "X" || markupUnit === "x" ? 6 : 2,
        }) !==
          formatNumberWithPrecision(markup, {
            precision: markupUnit === "X" || markupUnit === "x" ? 6 : 2,
          })
    );
  };

  const onFocusChange = () => {
    const markupUnit = useUser.user.markup_unit;
    if (markupUnit === "X" || markupUnit === "x") {
      const currentValue = getValues("markup") ?? "";
      const stripped = currentValue.replace(/^[Xx]\s*/, "");
      if (stripped !== currentValue) {
        setValue("markup", stripped);
      }
    }
  };

  const onBlurChange = (onBlur: any) => {
    if (getValues("markup") === "") {
      setValue("markup", markupAmount(markup, useUser.user.markup_unit));
    }
    const markupValue = getValues("markup");
    if (markupValue === ".") {
      setValue("markup", markupAmount("0.00", useUser.user.markup_unit));
    } else {
      const text = markupAmount(markupValue, useUser.user.markup_unit);
      setValue("markup", text);
    }
    onBlur();
  };
  return (
    <>
      <TextContainer>
        <Controller
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              inputLabel={
                useUser.user.markup_unit === "X" ||
                useUser.user.markup_unit === "x"
                  ? t(
                      "priceMode.input.multiplierMarkup.placeholderText",
                      "Multiplier Markup"
                    )
                  : t("priceMode.input.markup.placeholderText")
              }
              placeholder={t("priceMode.input.markup.placeholderText")}
              leftIcon={undefined}
              hideIconContainer={true}
              onFocus={onFocusChange}
              onBlur={() => onBlurChange(onBlur)}
              onChangeText={(text) => onChangeText(text, onChange)}
              value={value}
              error={undefined}
              keyboardType="numeric"
            />
          )}
          name="markup"
        />
        <Spacer size={SpaceType.Basic} />
        <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
          {useUser.user.markup_unit === "X" || useUser.user.markup_unit === "x"
            ? t(
                "priceMode.input.multiplierMarkup.infoText",
                "Example: If the base price of a stone is $100 and the markup is 3.00, the price will be updated to 300.00."
              )
            : t("priceMode.input.markup.infoText")}
        </VDBText>
        <Spacer size={SpaceType.Big} />
        <Button
          loading={!!updateUser.isLoading}
          type={ButtonType.primary}
          category={ButtonCategory.default}
          title={t("priceMode.applyButton.text")}
          disabled={!enableApplyButton}
          onPress={handleSubmit(onSubmit)}
        />
      </TextContainer>
    </>
  );
};

const Container = styled.View`
  margin-top: ${SpaceType.Extra}px;
  margin-left: ${SpaceType.Double}px;
  margin-right: ${SpaceType.Double}px;
`;

const CardMain = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Card = styled(TouchableOpacity)<CardProps>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 10px;
  border-color: ${({ borderColor }) => borderColor};
  border-width: 1px;
  margin-top: 10px;
  margin-bottom: 10px;
  height: 48px;
  min-height: 48px;
  padding-bottom: 12px;
`;

const ActiveView = styled.View`
  flex-direction: row;
  align-items: center;
`;

const Overlay = styled.View`
  flex-grow: 1;
  justify-content: center;
  align-items: center;
`;

const CenterContainer = styled.View`
  justify-content: center;
  flex-grow: 1;
`;
const TextContainer = styled.View`
  width: 100%;
  ${({ theme }) =>
    (theme.isDesktop || theme.isTablet) &&
    css`
      max-width: 343px;
    `}
`;

const ScreenText = styled.View`
  justify-content: flex-start;
  width: 100%;
  ${({ theme }) =>
    (theme.isDesktop || theme.isTablet) &&
    css`
      max-width: 343px;
    `}
`;

export default ChangeMarkupForProduct;
